import { useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTenantConfig } from './useTenantConfig';
import type { TenantIconConfig } from '../types';
import { apiService } from '../services/api';

// Default icon mappings
const DEFAULT_ICON_CONFIG: TenantIconConfig = {
  home: 'Home',
  settings: 'Settings',
  user: 'User',
  users: 'Users',
  logout: 'LogOut',
  save: 'Save',
  edit: 'Edit',
  delete: 'Trash2',
  add: 'Plus',
  cancel: 'X',
  refresh: 'RefreshCw',
  loading: 'Loader2',
  success: 'CheckCircle',
  error: 'AlertCircle',
  info: 'Info',
  help: 'HelpCircle',
  themeToggle: 'Moon', // Will be handled specially for Moon/Sun toggle
  language: 'Languages',
  chevronDown: 'ChevronDown',
  chevronLeft: 'ChevronLeft',
  chevronRight: 'ChevronRight',
  arrowLeft: 'ArrowLeft',
  arrowRight: 'ArrowRight',
  externalLink: 'ExternalLink',
  bot: 'Bot',
  messageCircle: 'MessageCircle',
  messageSquare: 'MessageSquare',
  shoppingCart: 'ShoppingCart',
  phone: 'Phone',
  mail: 'Mail',
  key: 'Key',
  shield: 'Shield',
  layout: 'Layout',
  folder: 'Folder',
  maximize: 'Maximize2',
  minimize: 'Minimize2',
  phoneOff: 'PhoneOff',
  check: 'Check',
};

/**
 * Hook to get tenant icon configuration and resolve icon components
 * @param tenantSlug Optional tenant slug, defaults to 'default'
 * @returns Object with getIcon function to resolve icon components
 */
export function useTenantIcons(tenantSlug?: string) {
  const { tenantData } = useTenantConfig(tenantSlug || 'default');

  // Merge tenant icon config with defaults
  const iconConfig = useMemo(() => {
    let tenantIconConfig: TenantIconConfig = {};
    
    if (tenantData?.config?.iconConfig) {
      try {
        const parsed = typeof tenantData.config.iconConfig === 'string'
          ? JSON.parse(tenantData.config.iconConfig)
          : tenantData.config.iconConfig;
        tenantIconConfig = parsed || {};
      } catch (error) {
        console.error('Failed to parse iconConfig:', error);
      }
    }

    return { ...DEFAULT_ICON_CONFIG, ...tenantIconConfig };
  }, [tenantData]);

  /**
   * Get icon component by key
   * @param iconKey The icon key (e.g., 'home', 'settings')
   * @param fallbackIcon Optional fallback icon name if key not found
   * @returns React component or null
   */
  const getIcon = useMemo(() => {
    return (iconKey: keyof TenantIconConfig, fallbackIcon?: string): React.ComponentType<any> | null => {
      const iconName = iconConfig[iconKey] || fallbackIcon;
      
      if (!iconName) {
        console.warn(`Icon key "${iconKey}" not found and no fallback provided`);
        return null;
      }

      try {
        const IconComponent = (LucideIcons as any)[iconName] as React.ComponentType<any>;
        if (!IconComponent) {
          console.warn(`Icon "${iconName}" not found in Lucide icons`);
          // Try fallback if provided
          if (fallbackIcon && fallbackIcon !== iconName) {
            const FallbackComponent = (LucideIcons as any)[fallbackIcon] as React.ComponentType<any>;
            return FallbackComponent || null;
          }
          return null;
        }
        return IconComponent;
      } catch (error) {
        console.error(`Error loading icon "${iconName}":`, error);
        return null;
      }
    };
  }, [iconConfig]);

  /**
   * Get icon name by key (useful for dynamic icon loading)
   * @param iconKey The icon key
   * @param fallbackIcon Optional fallback icon name
   * @returns Icon name string
   */
  const getIconName = useMemo(() => {
    return (iconKey: keyof TenantIconConfig, fallbackIcon?: string): string | null => {
      return iconConfig[iconKey] || fallbackIcon || null;
    };
  }, [iconConfig]);

  return useMemo(() => ({
    iconConfig,
    getIcon,
    getIconName,
  }), [iconConfig, getIcon, getIconName]);
}

